import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Single-use tokens (email verification, password reset, invitations) arrive
 * in the URL FRAGMENT: `/reset-password#token=…` (REV-878).
 *
 * A browser never sends a fragment to a server and never puts it in a Referer
 * header, so the token stays out of Traefik's access log and out of anything
 * the page loads. What is left is the address bar and the history entry, and
 * this hook clears both: it reads the token once, then replaces the current
 * history entry with the same path minus the fragment. From then on the token
 * lives only in component state and goes to the API in a POST body.
 *
 * It also accepts the older `?token=` query form (VerifyEmailPage always did)
 * and strips it the same way; a query string, unlike a fragment, has already
 * been sent to the server by the time this runs, which is why the emails no
 * longer use it.
 *
 * A reload after the token has been cleared finds no token, and the page shows
 * its invalid-link state. That is the intended trade: the link in the email
 * still works, and a URL that no longer holds the token cannot leak it.
 *
 * @returns {string | null} the token, or null if the URL carried none
 */
export function useSingleUseToken(): string | null {
  const location = useLocation();
  const navigate = useNavigate();

  // Read once, on the first render — before the URL is rewritten below.
  const [found] = useState(() => {
    const fromFragment = new URLSearchParams(location.hash.replace(/^#/, '')).get('token');
    const fromQuery = new URLSearchParams(location.search).get('token');
    return {
      token: fromFragment || fromQuery || null,
      inUrl: Boolean(fromFragment || fromQuery),
    };
  });

  // `replace` rewrites the current history entry (history.replaceState) rather
  // than pushing a new one, so Back does not return to the tokenised URL.
  //
  // A passive effect, not a layout effect, on purpose: the router subscribes
  // to history in its own layout effect, which runs AFTER a child's. A replace
  // from a child's layout effect rewrote the browser URL but never reached the
  // router, which kept rendering the old location with the token in it.
  useEffect(() => {
    if (found.inUrl) {
      void navigate({ pathname: location.pathname }, { replace: true });
    }
    // Once, on mount: the token is already captured in state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return found.token;
}

/**
 * The target of an email sent before REV-878, which put the token in the path
 * (`/reset-password/:token`, `/verify-email/:token`, `/invites/:token/accept`).
 *
 * The request for this page has already carried the token to the server; that
 * cannot be undone from here. What this does is stop it going any further: it
 * replaces the history entry with the fragment form at once, before any API
 * call is made, so the page that then loads posts the token in a body and
 * strips the fragment in turn. Neither the path nor the fragment form is left
 * in history.
 *
 * Deprecated with the API's path-token routes. Remove once 7 days (the longest
 * token lifetime, an invitation's) have passed since the fragment-link emails
 * reached production.
 */
export function LegacyTokenRedirect({ to }: { to: string }) {
  const { token } = useParams<{ token: string }>();
  const target = token ? `${to}#token=${encodeURIComponent(token)}` : to;
  return <Navigate to={target} replace />;
}
